'use client';
import { useEffect, useState } from 'react';
import { cn } from '../cn';
import type { DocumentListShellProps } from './document-list-shell-types';
import { DocumentListShellHeader } from './document-list-shell-header';
import { DocumentListShellTabs } from './document-list-shell-tabs';
import { DocumentListShellCustomize } from './document-list-shell-customize';

/**
 * Wrapper layout pentru pagini de listing documente fiscale (facturi,
 * cheltuieli, NIR, încasări, etc.) construit peste `<DataTable />`. Furnizează:
 * page header, KPI rail, suggestion rail, aging report, saved views tabs,
 * filter bar (search + chips + customize), drawer slot. Children = de obicei
 * `<DataTable />`.
 *
 * Pattern uniform pentru 8+ pagini de listing — eliminări 6 000+ linii.
 */
export function DocumentListShell({
  // Header
  header,
  title,
  subtitle,
  primaryAction,
  headerActions,
  // Period
  period,
  onPeriodChange,
  hidePeriodSelector,
  // Slots
  kpis,
  suggestionRail,
  agingReport,
  drawer,
  // Tabs
  views,
  activeView: activeViewControlled,
  onViewChange,
  onSavedViewAdd,
  onSavedViewDelete,
  // Filter bar
  search,
  onSearchChange,
  searchPlaceholder = 'Caută...',
  filterChips,
  filterBarExtra,
  // Customize
  customizeWidgets,
  hiddenWidgets: hiddenWidgetsControlled,
  onHiddenWidgetsChange,
  // Persist
  persistKey,
  // A11y
  ariaLabel,
  className,
  children,
}: DocumentListShellProps) {
  // Persist activeView dacă persistKey e setat și activeView nu e controlat
  const [activeViewInternal, setActiveViewInternal] = useState<string>(() => {
    if (!persistKey) return views?.[0]?.id ?? '';
    if (typeof window === 'undefined') return views?.[0]?.id ?? '';
    try {
      const v = window.localStorage.getItem(`${persistKey}:activeView`);
      return v ?? views?.[0]?.id ?? '';
    } catch { return views?.[0]?.id ?? ''; }
  });
  const activeView = activeViewControlled ?? activeViewInternal;
  const handleViewChange = (id: string) => {
    if (activeViewControlled === undefined) {
      setActiveViewInternal(id);
      if (persistKey && typeof window !== 'undefined') {
        try { window.localStorage.setItem(`${persistKey}:activeView`, id); } catch {}
      }
    }
    onViewChange?.(id);
  };

  // Persist hiddenWidgets
  const [hiddenWidgetsInternal, setHiddenWidgetsInternal] = useState<Set<string>>(() => {
    if (!persistKey || typeof window === 'undefined') return new Set();
    try {
      const raw = window.localStorage.getItem(`${persistKey}:hiddenWidgets`);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch { return new Set(); }
  });
  const hiddenWidgets = hiddenWidgetsControlled ?? hiddenWidgetsInternal;
  const handleHiddenWidgetsChange = (next: Set<string>) => {
    if (hiddenWidgetsControlled === undefined) setHiddenWidgetsInternal(next);
    if (persistKey && typeof window !== 'undefined') {
      try { window.localStorage.setItem(`${persistKey}:hiddenWidgets`, JSON.stringify(Array.from(next))); } catch {}
    }
    onHiddenWidgetsChange?.(next);
  };

  // Helper pentru consumer: utility de citit dacă un widget e ascuns (apelat din kpis slot)
  // Kpis primește toate widget-urile dar consumer-ul filtrează după hiddenWidgets
  // (e mai flexibil decât să facem aici filter mecanic — consumer poate să rendere
  // KPI-uri non-toggleable mereu)

  return (
    <section
      className={cn('flex flex-col gap-3 p-4 sm:p-6', className)}
      aria-label={ariaLabel}
    >
      {header ?? (
        <DocumentListShellHeader
          title={title}
          subtitle={subtitle}
          primaryAction={primaryAction}
          period={period}
          onPeriodChange={onPeriodChange}
          hidePeriodSelector={hidePeriodSelector}
          headerActions={headerActions}
        />
      )}

      {kpis && <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">{kpis}</div>}

      {agingReport && <div>{agingReport}</div>}

      {suggestionRail && <div className="overflow-x-auto">{suggestionRail}</div>}

      {views && views.length > 0 && (
        <DocumentListShellTabs
          views={views}
          activeView={activeView}
          onViewChange={handleViewChange}
          onSavedViewAdd={onSavedViewAdd}
          onSavedViewDelete={onSavedViewDelete}
        />
      )}

      {(onSearchChange || filterChips || customizeWidgets) && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-neutral-200 bg-white px-3 py-2" role="search">
          {onSearchChange && (
            <label className="flex min-w-0 flex-1 items-center gap-1.5">
              <SearchIcon />
              <span className="sr-only">{searchPlaceholder}</span>
              <input
                type="search"
                value={search ?? ''}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                className="min-w-0 flex-1 border-0 bg-transparent text-sm text-neutral-900 outline-none placeholder:text-neutral-400"
              />
            </label>
          )}
          {filterChips && <div className="flex flex-wrap items-center gap-1.5 border-l border-neutral-100 pl-3">{filterChips}</div>}
          {filterBarExtra && <div className="flex flex-wrap items-center gap-1.5">{filterBarExtra}</div>}
          {customizeWidgets && customizeWidgets.length > 0 && (
            <DocumentListShellCustomize
              widgets={customizeWidgets}
              hidden={hiddenWidgets}
              onChange={handleHiddenWidgetsChange}
            />
          )}
        </div>
      )}

      <div>{children}</div>

      {drawer}
    </section>
  );
}

function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-neutral-400" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}
